//@ts-strict-ignore
import {
  AfterViewInit,
  Component,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { SohoBusyIndicatorDirective } from 'ids-enterprise-ng';
import { LMContextService } from '@services/lm-context.service';
import {
  LMBusyIndicatorService,
  LMErrorUtilService
} from '@app/services';

import {
  LMReportSecurityService,
  LMReportsSecurityListComponent
} from '@app/components';
import { LMNotAccessibleItems, LMReportSecurityRequest, LMReportSecurityResponse, LMSecurityReport } from '../lm-reports';

@Component({
  selector:    'lm-reports-security',
  standalone: false,
  templateUrl: './lm-reports-security.component.html',
  styleUrls:   [ 'lm-reports-security.component.scss' ],
  providers:   [],
  })
export class LMReportsSecurityComponent implements AfterViewInit, OnInit, OnDestroy {

  // ---------------------
  // Page layout variables
  // ---------------------
  private screenId = 'lm-report-security-form';
  displayDataGrid = false;
  busyTimeOut: number;
  reportsArray: Array<LMSecurityReport> = [];
  notAccessibleItems: LMNotAccessibleItems;
  notAccessibleItemsKeys: Array<string>;
  dataView: LMDataView;
  validateChildrenStateOnClose = false;

  @Input() options: LMReportSecurityRequest;

  @ViewChild(SohoBusyIndicatorDirective) busyIndicator: SohoBusyIndicatorDirective;
  @ViewChild(forwardRef(() =>  LMReportsSecurityListComponent)) lmReportsListComponents: LMReportsSecurityListComponent;

  @HostBinding('class.form-layout-compact') @Input() useCompactFormLayout = true;

  constructor(
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    public    lmContextService: LMContextService,
    private   lmErrorUtilService: LMErrorUtilService,
    private   lmReportSecurityService: LMReportSecurityService,
  ) {
  }

  ngOnInit() {
    // this.lmLoggerService.time({ id: '>> Reports Catalog' });
    this.busyTimeOut = this.lmContextService.uiLoadingIndicatorTimeout;
  }

  ngAfterViewInit() {
    this.retrieveReportsCatalogSpecJSON();
  }

  ngAfterViewChecked() {
  }

  ngOnDestroy() {
  }

  private retrieveReportsCatalogSpecJSON(): void {
    this.lmBusyIndicatorService.showBusyIndicator(this.busyIndicator);
    const postContentIn = { role: this.options.role };

    this.lmReportSecurityService.getReportSecurityData(postContentIn).subscribe((response) => {
      if (response?.status === 'SUCCESS') {
        this.unpackSpec(response);
      } else {
        this.lmErrorUtilService.handleUnexpectedError(response);
      }
      this.displayDataGrid = true;
      this.lmBusyIndicatorService.hideBusyIndicator(this.busyIndicator);
    });
  }

  private unpackSpec(spec: LMReportSecurityResponse): void {
    this.notAccessibleItems = spec['notAccessibleItems'];
    this.notAccessibleItemsKeys = Object.keys(this.notAccessibleItems);
    this.dataView = spec['dataView'];
    this.reportsArray = spec['reportsJSONArray'];
    this.setInaccessibleItemState(this.reportsArray);
  }

  private setInaccessibleItemState(items: Array<LMSecurityReport>) {
    if (items) {
      items.forEach(report => {
        this.notAccessibleItemsKeys.forEach(key => {
          if (!this.notAccessibleItems[key] && report.id === key) {
            this.notAccessibleItems[key] = report.title;
            this._setTreeState(report.children, report);
          }
          this.setInaccessibleItemState(report.children);
        });
      });
    }
  }

  private setTreeState(reports: LMSecurityReport[]) {
    for (const report of reports) {
      if (report.children) {
        this._setTreeState(report.children, null);
      }
    }
  }

  private _setTreeState(items: LMSecurityReport[], parent: LMSecurityReport) {
    if (items) {
      for (const item of items) {
        item.expanded = false;
        item.disabled = parent?.notAccessible || parent?.disabled;
        if (item.children) {
          this._setTreeState(item.children, item);
        }
      }
    }
  }

  onInaccessibleItemRemove(item: string) {
    this.validateChildrenStateOnClose = true;
    delete this.notAccessibleItems[item];

    const report = this.findReport(item, this.reportsArray);

    if (report) {
      this.updateInaccessibleChildren(report, false);
    }

    this.notAccessibleItemsKeys = Object.keys(this.notAccessibleItems);
    this.setInaccessibleItemState(this.reportsArray);

    if (this.lmReportsListComponents) {
      this.lmReportsListComponents.updateInaccessible(item);
    }
  }

  public getDataView(): LMDataView {
    const field = this.dataView.fields['ParamConfiguration'];
    let keys;

    if (this.validateChildrenStateOnClose) {
      const sb: Array<string> = [];
      this.buildInaccessibleItems(sb);
      keys = Object.keys(this.notAccessibleItems);

      sb.forEach(s => {
        if (!keys.includes(s)) {
          keys.push(s);
        }
      });
    } else {
      keys = Object.keys(this.notAccessibleItems);
    }

    keys.sort();
    const sb1: Array<string> = [];

    keys.forEach(k => {
      sb1.push(k);
    });

    field.value = sb1.join('\n');

    return this.dataView;
  }

  onNotAccessibleClicked($event){
    this.validateChildrenStateOnClose = true;
    if ($event.data.notAccessible) {
      this.notAccessibleItems[$event.data.id] = $event.data.title;
    } else {
      if (this.notAccessibleItemsKeys.indexOf($event.data.id) > -1) {
        delete this.notAccessibleItems[$event.data.id];
      } else {
        console.log('Error - item not found: ' + $event.data.id);
      }
    }

    this.updateInaccessibleChildren($event.data, $event.data.notAccessible);
    this.notAccessibleItemsKeys = Object.keys(this.notAccessibleItems);
  }


  private updateInaccessibleChildren(item: LMSecurityReport, inaccessible: boolean) {
    if (item.children) {
      for (const child of item.children) {
        if (inaccessible) {
          this.notAccessibleItems[child.id] =  child.title;
        } else {
          delete this.notAccessibleItems[child.id];
        }
        this.updateInaccessibleChildren(child, inaccessible);
      }
    }
  }

  private findReport(id: string, data: Array<LMSecurityReport>) : LMSecurityReport {
    for (let i = 0; i < data.length; i++) {
      const report = data[i];
      if (report.id === id) {
        return report;
      }

      if (report.children) {
        const child = this.findReport(id, report.children);
        if (child) {
          return child;
        }
      }
    }

    return null;
  }

  private buildInaccessibleItems(properties: Array<string>) {
      this._checkChildren(undefined, this.reportsArray, properties);
  }

  private _checkChildren(parent: LMSecurityReport, children: Array<LMSecurityReport>, properties: Array<string>) {
    if (children) {
      children.forEach(child => {
        if (parent?.notAccessible) {
          child.notAccessible = true;
        }
        if (child.notAccessible) {
          properties.push(child.id);
        }
        this._checkChildren(child, child.children, properties);
      });
    }
  }
}
